import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, ArrowRight, Sparkles, Brain, Activity } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1523305846158-488dedcf8629?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  return (
    <div className="min-h-screen celestial overflow-hidden">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <span className="text-2xl font-serif text-foreground">My<span className="text-primary">N</span>sight</span>
        <Link to="/auth" data-testid="signin-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign in</Link>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overline mb-6">Your personal cosmic companion</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Meet the you<br />written in the <span className="text-primary italic">stars</span>,<br />and everywhere else.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
            MyNsight weaves your astrology, Human Design, numerology, Chinese zodiac and psychology into one living portrait —
            then remembers you, notices your patterns, and grows with you.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/setup" data-testid="begin-ritual-btn"
              className="gold-btn px-8 py-4 rounded-full font-medium flex items-center gap-2">
              Begin the ritual <ArrowRight size={18} />
            </Link>
            <Link to="/auth" className="px-8 py-4 rounded-full border border-border hover:border-primary/50 transition-colors">
              I already have a companion
            </Link>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
          className="md:col-span-5 relative">
          <div className="relative rounded-[2rem] overflow-hidden panel">
            <img src={HERO} alt="Full moon over dark mountains" className="w-full h-[440px] object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-4">
              <p className="font-mono text-[11px] uppercase tracking-widest text-primary mb-1">Manifestor · 7 · Life Path 9</p>
              <p className="text-sm text-foreground/90">"Your urge to initiate meets your fear of missing out. Ride the wave first."</p>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-28 grid md:grid-cols-3 gap-6">
        {[
          { icon: Sparkles, t: "Unified Blueprint", d: "Not six separate readings — one synthesized portrait of your drives, fears, strengths and blind spots." },
          { icon: Brain, t: "A Companion That Remembers", d: "Persistent memory that references your past states, choices and turning points." },
          { icon: Activity, t: "Patterns Over Time", d: "Daily check-ins and mood tracking your companion actually reads and reflects back." },
        ].map((f, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="panel p-8">
            <f.icon className="text-primary mb-4" size={22} />
            <h3 className="font-serif text-2xl mb-2">{f.t}</h3>
            <p className="text-muted-foreground leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
        <span className="font-serif text-lg text-primary">My<span className="text-foreground">Nsight</span></span>
        <div className="flex gap-6">
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link to="/auth" className="hover:text-foreground transition-colors">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
